import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, FlaskConical, Package } from 'lucide-react';
import { products, heroImage } from '../mock';
import ProductCard from '../components/ProductCard';
import Seo, { SITE_URL, SITE_NAME } from '../components/Seo';

const Home = () => {
  const featured = ['ghk-cu', 'klow-blend-80mg', 'nad-plus-500mg', 'retatrutide', 'wolverine-blend-20mg']
    .map(s => products.find(p => p.slug === s)).filter(Boolean);
  const catalog = products.slice(0, 8);

  const orgLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    email: 'vialratpayments@gmail.com',
    address: { '@type': 'PostalAddress', addressCountry: 'CA' }
  };
  const siteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL
  };

  return (
    <main>
      <Seo
        title="Research Peptides Canada | Vial Rat Research Co."
        description="Vial Rat Research Co. offers research peptides and laboratory materials shipped from within Canada. Explore our Canadian research catalogue online."
        canonicalPath="/"
        jsonLd={[orgLd, siteLd]}
      />
      {/* Hero */}
      <section className="relative hero-swirl overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32 grid md:grid-cols-2 items-center gap-14">
          <div>
            <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-6">EST. CANADA — SPECIMEN GRADE</div>
            <h1 className="font-serif-display text-white leading-[0.95] text-[64px] md:text-[92px] tracking-tight">
              WELCOME<br />TO THE <span className="text-gradient-pink">LAB</span>
            </h1>
            <div className="mt-8 font-serif-display text-2xl text-white">VIAL RAT RESEARCH CO.</div>
            <p className="mt-3 text-neutral-400 max-w-md">Canadian research materials with a little attitude.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/shop" className="btn-pink px-7 py-4 rounded font-mono-label text-[11px] inline-flex items-center gap-3">
                SHOP THE LAB <ArrowRight size={16} />
              </Link>
              <Link to="/shop" className="btn-outline px-7 py-4 rounded font-mono-label text-[11px]">
                VIEW RESEARCH CATALOG
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-6 bg-[#ff2fa1]/10 blur-3xl rounded-full" />
            <img src={heroImage} alt="Vial Rat mascot in a lab coat holding a research vial"
              className="relative rounded-2xl border border-white/10 shadow-[0_40px_100px_-20px_rgba(255,47,161,0.35)]" />
          </div>
        </div>
      </section>

      {/* Feature stripe */}
      <section className="border-y border-white/5 bg-[#0c0c0e]">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-3 grid-hairline">
          {[
            { icon: MapPin, title: 'CANADA BASED', desc: 'Shipped from within Canada.' },
            { icon: FlaskConical, title: 'RESEARCH FOCUSED', desc: 'Canadian peptide research materials. Explore our growing catalogue of research peptides and related laboratory materials, shipped from within Canada.' },
            { icon: Package, title: 'DISCREET SHIPPING', desc: 'Plain packaging. Shipped within Canada. Orders are packaged discreetly and securely for Canadian delivery.' },
          ].map((f, i) => (
            <div key={i} className="px-8 py-10">
              <f.icon className="text-[#ff2fa1]" size={26} />
              <div className="mt-5 font-mono-label text-[12px] text-white">{f.title}</div>
              <p className="mt-2 text-sm text-neutral-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">SELECTED SPECIMENS</div>
            <h2 className="font-serif-display text-white text-4xl md:text-5xl">Featured from the Lab</h2>
          </div>
          <Link to="/shop" className="hidden md:inline-flex items-center gap-2 font-mono-label text-[11px] text-[#ff2fa1] hover:text-white transition-colors">
            ALL PRODUCTS <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
          {featured.map(p => <ProductCard key={p.slug} product={p} variant="featured" />)}
        </div>
      </section>

      {/* Full catalog */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="mb-12">
          <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">FULL CATALOG</div>
          <h2 className="font-serif-display text-white text-4xl md:text-5xl">EXPLORE THE LAB</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {catalog.map(p => <ProductCard key={p.slug} product={p} />)}
        </div>
        <Link to="/shop" className="mt-10 block text-center btn-pink-outline py-5 rounded font-mono-label text-[11px]">
          BROWSE FULL RESEARCH CATALOG
        </Link>
      </section>
    </main>
  );
};

export default Home;
